import { ChangeDetectorRef, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Navigate } from '@ngxs/router-plugin';
import { Store } from '@ngxs/store';
import { TenantDataService } from 'app/config/services/tenant-data.service';
import { CertificateDialogComponent } from 'app/library/certificate-record/components/dialogs/edit-certificate-dialog/certificate-dialog.component';
import { CertificateRecord } from 'app/library/certificate-record/models/certificate-record';
import { CertificateRecordService } from 'app/library/certificate-record/services/certificate-record.service';
import { CertificateType } from 'app/library/certificate/enums/certificate-type.enum';
import { EditCertificateDialogComponent } from 'app/library/certifications/components/dialogs/edit-certificate-dialog/edit-certificate-dialog.component';
import { MaxBrainEvent } from 'app/library/event/models/event.model';
import { ModuleManagersService } from 'app/library/module-managers/services/module-managers.service';
import { ToDoTypeEnum } from 'app/library/to-do/enums/to-do-type.enum';
import { ToDo } from 'app/library/to-do/models/to-do.model';
import { TrainingApprovalRequestStatus } from 'app/library/training-approval-request/models/training-approval-request-status';
import { TrainingApprovalRequest } from 'app/library/training-approval-request/models/training-approval-request.model';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { fuseAnimations } from 'app/projects/fuse/src/lib/animations';
import { FeatureSwitchName } from 'app/projects/shared/src/lib/enums/feature-switch.enum';
import { FeatureSwitchPipe } from 'app/projects/shared/src/lib/pipes/feature-switch.pipe';
import { User } from 'app/projects/user/src/lib/models/user';
import { UserLearningProgress } from 'app/projects/user/src/lib/models/user-learning-progress.model';
import { UserToDo } from 'app/projects/user/src/lib/models/user-to-do.model';
import { UserToDosStatistics } from 'app/projects/user/src/lib/models/user-to-dos-statistics.model';
import { UserToDoPerformanceReview } from 'app/projects/user/src/lib/models/user-todo-performance-review.model';
import { UserApiService } from 'app/projects/user/src/lib/services/user.api-service';
import { PerformancePersonalReviewStatus } from 'app/library/performance-personal-review/models/performance-personal-review-status';

@Component({
    selector: 'app-learner-profile',
    templateUrl: './learner-profile.component.html',
    styleUrls: ['./learner-profile.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations,
})
export class LearnerProfileComponent extends UnsubscribeOnDestroy implements OnInit {
    @Input()
    profile: User;

    userLearningProgress: UserLearningProgress;
    userToDosStatistics: UserToDosStatistics;
    toDoList: UserToDo;
    performanceReviewDoList: UserToDoPerformanceReview;

    eventsResponse: { events: MaxBrainEvent[]; totalCount: number };
    events: MaxBrainEvent[] = [];

    certificatesResponse: { certificates: CertificateRecord[]; totalCountRequireAction: number; newCount: number; totalCount: number };
    certificates: CertificateRecord[] = [];

    trainingApprovalRequestsResponse: { requests: TrainingApprovalRequest[]; activeCount: number };
    trainingApprovalRequests: TrainingApprovalRequest[] = [];

    isGlobalCalendarFeatureEnabled: boolean = false;
    isEventsTabVisible: boolean = false;
    isGradebookFeatureEnabled: boolean = false;
    isCertificateOfAttendanceFeatureEnabled: boolean = false;
    isTrainingApprovalRequestFeatureEnabled: boolean = false;
    isCertificateMgmtFeatureEnabled: boolean = false;
    isToDoFeatureEnabled: boolean = false;
    isPerformanceFeedbackFormFeatureEnabled: boolean = false;
    isCertificatesTabVisible: boolean = false;
    canUserManageCertificates: boolean = false;
    isLoading: boolean = false;

    CertificateType = CertificateType;
    TrainingApprovalRequestStatus = TrainingApprovalRequestStatus;
    ToDoTypeEnum = ToDoTypeEnum;
    performancePersonalReviewStatus = PerformancePersonalReviewStatus;

    private async _fetchCertificates(shouldUserHaveCertificate: boolean): Promise<void> {
        try {
            this.certificatesResponse = await this._userApiService.getLearnerProfileCertificates();
            this.certificates = this.certificatesResponse.certificates;
        } catch (error) {}

        if ((shouldUserHaveCertificate && this.certificates.length > 0) || (!shouldUserHaveCertificate && this.canUserManageCertificates)) {
            this.isCertificatesTabVisible = true;
        } else {
            this.isCertificatesTabVisible = false;
        }
    }

    private async _fetchApprovalRequests(): Promise<void> {
        try {
            this.trainingApprovalRequestsResponse = await this._userApiService.getLearnerProfileApprovalRequests();
            this.trainingApprovalRequests = this.trainingApprovalRequestsResponse.requests;
        } catch (error) {}
    }

    private get hasAnyCertificateFeatureEnabled(): boolean {
        return this.isCertificateMgmtFeatureEnabled || this.isCertificateOfAttendanceFeatureEnabled || this.isGradebookFeatureEnabled;
    }

    constructor(
        private _userApiService: UserApiService,
        private _router: Router,
        private _featureSwitchpipe: FeatureSwitchPipe,
        private _certificateRecordService: CertificateRecordService,
        private _matDialog: MatDialog,
        private _tenantDataService: TenantDataService,
        private _moduleManagersService: ModuleManagersService,
        private _cd: ChangeDetectorRef,
        private _store: Store
    ) {
        super();

        this.isGlobalCalendarFeatureEnabled = this._featureSwitchpipe.transform(FeatureSwitchName.GlobalCalendar);
        this.isGradebookFeatureEnabled = this._featureSwitchpipe.transform(FeatureSwitchName.Gradebook);
        this.isCertificateOfAttendanceFeatureEnabled = this._featureSwitchpipe.transform(FeatureSwitchName.CertificateOfAttendance);
        this.isTrainingApprovalRequestFeatureEnabled = this._featureSwitchpipe.transform(FeatureSwitchName.TrainingApprovalRequest);
        this.isCertificateMgmtFeatureEnabled = this._featureSwitchpipe.transform(FeatureSwitchName.CertificateManagement);
        this.isToDoFeatureEnabled = this._featureSwitchpipe.transform(FeatureSwitchName.ToDo);
        this.isPerformanceFeedbackFormFeatureEnabled = this._featureSwitchpipe.transform(FeatureSwitchName.PerformanceFeedbackForm);
        this.canUserManageCertificates = this._tenantDataService.tenantConfig?.certificate_management?.users_can_manage_certificates;
    }

    async ngOnInit(): Promise<void> {
        this.isLoading = true;

        // Always fetched in parallel
        const learningProgressPromise = this._userApiService.getLearningProgress();
        const eventsPromise = this._userApiService.getLearnerProfileEvents();

        // Conditional parallel promises
        let certificatesPromise: Promise<void> | undefined;
        if (this.isCertificateMgmtFeatureEnabled && this.canUserManageCertificates) {
            certificatesPromise = this._fetchCertificates(false);
        } else if (this.hasAnyCertificateFeatureEnabled) {
            certificatesPromise = this._fetchCertificates(true);
        }

        let approvalRequestsPromise: Promise<void> | undefined;
        if (this.isTrainingApprovalRequestFeatureEnabled) {
            approvalRequestsPromise = this._fetchApprovalRequests();
        }

        let toDosPromise: Promise<UserToDo> | undefined;
        if (this.isToDoFeatureEnabled) {
            toDosPromise = this._userApiService.getLearnerProfileToDos();
        }

        let userToDoPerformanceReviewPromise: Promise<UserToDoPerformanceReview>;
        if (this.isPerformanceFeedbackFormFeatureEnabled) {
            userToDoPerformanceReviewPromise = this._userApiService.getLearnerProfilePerformanceReviews();
        }

        // Await all promises in parallel
        const [learningProgress, events, toDos, performanceReviews] = await Promise.all([
            learningProgressPromise,
            eventsPromise,
            toDosPromise,
            userToDoPerformanceReviewPromise,
            certificatesPromise,
            approvalRequestsPromise,
        ]);

        // Assign resolved data
        this.userLearningProgress = learningProgress;
        this.eventsResponse = events;
        this.events = events?.events ?? [];

        if (toDos) {
            this.toDoList = toDos;
        }

        if (userToDoPerformanceReviewPromise) {
            this.performanceReviewDoList = performanceReviews;
        }

        this.isEventsTabVisible = this.isGlobalCalendarFeatureEnabled || this.events.length > 0;

        this.isLoading = false;
        this._cd.detectChanges();
    }

    goToEvent(event?: MaxBrainEvent): void {
        if (event?.type === 'public' || !event) {
            this._router.navigate(['/calendar'], { queryParams: { eventId: event?.uniqueId } });
        } else {
            this._router.navigate(['/modules', event.module.id, 'schedule']);
        }
    }

    navigateByToDoType(todo: ToDo): void {
        switch (todo.type) {
            case ToDoTypeEnum.MODULE_TO_DO:
                this._moduleManagersService.goToModule(this.profile.id, todo.moduleToDo.module, 'overview', true);
                break;
            case ToDoTypeEnum.CERTIFICATE_TO_DO:
                this.openCertificate(todo.certificateToDo.certificate);
                break;
            case ToDoTypeEnum.QUIZ_TO_DO:
            case ToDoTypeEnum.SURVEY_TO_DO:
                this._store.dispatch(new Navigate([`modules/${todo.sectionToDo.section.moduleId}/content/sections/${todo.sectionToDo.section.id}`]));
                break;
            case ToDoTypeEnum.PERSONAL_REVIEW_TO_DO:
                this._store.dispatch(new Navigate([`profile/performance-review/${todo.personalReviewToDo.personalReview.id}`]));
                break;
            case ToDoTypeEnum.TEAM_REVIEW_TO_DO:
                this._store.dispatch(new Navigate([`my-teams/team/${todo.teamReviewToDo.teamReview.team.id}/team-reviews/${todo.teamReviewToDo.teamReview.id}`]));
                break;
            case ToDoTypeEnum.APPROVAL_REQUEST_TO_DO:
                this._router.navigate(['/profile/tab/approval-requests', { outlets: { modal: ['request', todo.approvalRequestToDo.approvalRequest.id] } }], {
                    queryParams: { listType: 'user' },
                });
                break;
        }
    }

    goToCertificates(): void {
        this._router.navigate(['/profile/tab/certificates']);
    }

    goToApprovalRequests(): void {
        this._router.navigate(['/profile/tab/approval-requests']);
    }

    goToPerformanceReviews(): void {
        this._router.navigate(['/profile/tab/performance-reviews']);
    }

    goToToDos(): void {
        this._router.navigate(['/profile/to-dos']);
    }

    navigateToPerformanceReview(reviewId: string): void {
        this._store.dispatch(new Navigate([`/profile/performance-review/${reviewId}`]));
    }

    openApprovalRequest(approvalrequest: TrainingApprovalRequest): void {
        this._router.navigate(['/profile/tab/approval-requests', { outlets: { modal: ['request', approvalrequest.id] } }], { queryParams: { listType: 'user' } });
    }

    async openCertificate(certificate: CertificateRecord): Promise<void> {
        if (certificate.type === CertificateType.MODULE_PARTICIPANT_CERTIFICATION || certificate.type === CertificateType.USER_CERTIFICATION) {
            await this._certificateRecordService.fetchCertificateRecord(certificate.id);
            const certificateRecord = this._certificateRecordService.getCertificateRecord();

            const dialogRef = this._matDialog.open(EditCertificateDialogComponent, {
                width: '1100px',
                panelClass: 'full-screen-modal',
            });

            dialogRef.componentInstance.title = 'CERTIFICATE.SINGULAR';
            dialogRef.componentInstance.certificateRecord = certificateRecord;
            dialogRef.componentInstance.isViewOnly = !this.canUserManageCertificates || !!certificateRecord.moduleUser?.id;
        } else {
            const dialogRef = this._matDialog.open(CertificateDialogComponent, {
                width: '650px',
            });

            dialogRef.componentInstance.certificateRecord = certificate;
            dialogRef.componentInstance.userId = this.profile.id;
        }
    }
}
